"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  LineChart,
  type LineRow,
} from "@/components/tool-kit/charts/line-chart";
import {
  Checkbox,
  Notice,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { MAX_SERIES } from "@/lib/tool-kit/charts/color";
import { type NumberFormat, valueText } from "@/lib/tool-kit/charts/format";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { numericLabels } from "@/lib/tool-kit/charts/lines";
import {
  type LabeledTable,
  parseCell,
  parseLabeledRows,
  seriesNames,
} from "@/lib/tool-kit/charts/parse";

/** English strings. A translated content file overrides these through `ui`. */
export const LINE_GRAPH_MAKER_STRINGS = {
  dataLabel: "Your data — one x value per line: x, y",
  dataHint:
    "Add more y values on a line to draw more lines. Separate cells with commas, tabs or semicolons — use semicolons if your numbers have decimal commas. A first line without numbers names the lines. Leave a cell empty for a missing value.",
  exampleData:
    "Week, Class A, Class B\n1, 18, 15\n2, 21, 17\n3, 24, 20\n4, 26, 24\n5, 29, 25\n6, 31, 28",
  firstLineHeader: "The first line names the columns",
  exampleTitle: "Average Daily Reading Time by Week",
  exampleXTitle: "Week",
  exampleYTitle: "Minutes per day",
  pointsDetected: "one:{count} x value|other:{count} x values",
  seriesDetected: "one:{count} line|other:{count} lines",
  seriesDefault: "Series {number}",
  xModeLabel: "X values",
  xModeAuto: "Detect automatically",
  xModeCategory: "Categories, evenly spaced",
  xModeNumeric: "Numbers, spaced to scale",
  markers: "Mark each data point",
  startAtZero: "Start the y-axis at 0",
  labelsAndStyle: "Labels and style",
  chartTitle: "Chart title",
  xAxisTitle: "X-axis title",
  yAxisTitle: "Y-axis title",
  axisTitlePlaceholderX: "X",
  axisTitlePlaceholderY: "Value",
  colorLabel: "Line color (the first line)",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  valueLabels: "Show the value at each point",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and the gridlines out of the plot, and sets the figure number in bold and the title in italics above the chart — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the data as a table",
  xColumn: "X",
  chartLabel: "Line graph",
  chartDescription:
    "Line graph of {series} across {points}, from {first} to {last}. Values range from {min} to {max}.",
  enterData:
    "Enter at least two lines, each with an x value and a number, to draw the graph.",
  notNumeric:
    "Some x values are not numbers, so the points are spaced evenly as categories.",
  sortedByX: "The points are joined in order of x, smallest first.",
  gapsNote:
    "Empty cells leave a gap in the line rather than joining across it.",
  skippedLines:
    "one:Line {lines} has no number and was left out.|other:Lines {lines} have no numbers and were left out.",
  tooManySeries:
    "Only the first {max} lines are drawn — beyond that, colors can no longer be told apart.",
} as const;

type Strings = typeof LINE_GRAPH_MAKER_STRINGS;
type XMode = "auto" | "category" | "numeric";

const MISSING = "\u2014";

/** Rows ready to draw: x read as a number when the axis is numeric, sorted by it. */
function prepareRows(table: LabeledTable, mode: XMode, seriesCount: number) {
  const rows: LineRow[] = table.rows.map((row) => ({
    label: row.label,
    x: null,
    values: row.values.slice(0, seriesCount),
  }));
  const xs = numericLabels(
    rows.map((row) => row.label),
    parseCell
  );
  const numeric = mode !== "category" && xs !== null;
  let sorted = false;
  if (numeric && xs) {
    for (const [index, row] of rows.entries()) {
      row.x = xs[index];
    }
    sorted = xs.some((x, index) => index > 0 && x < xs[index - 1]);
    rows.sort((a, b) => (a.x ?? 0) - (b.x ?? 0));
  }
  return { rows, numeric, sorted, fellBack: mode === "numeric" && !numeric };
}

/** The `<desc>` a screen reader announces. */
function describeLines(
  rows: LineRow[],
  names: string[],
  strings: Strings,
  locale: string,
  fmt: NumberFormat
): string {
  const all = rows
    .flatMap((row) => row.values)
    .filter((v): v is number => v !== null);
  return t(strings.chartDescription, {
    series: tPlural(strings.seriesDetected, locale, names.length, {
      count: fmt(names.length),
    }),
    points: tPlural(strings.pointsDetected, locale, rows.length, {
      count: fmt(rows.length),
    }),
    first: rows[0]?.label ?? "",
    last: rows.at(-1)?.label ?? "",
    min: all.length > 0 ? valueText(fmt, Math.min(...all)) : "",
    max: all.length > 0 ? valueText(fmt, Math.max(...all)) : "",
  });
}

export default function LineGraphMakerWidget() {
  const strings = useToolUi(LINE_GRAPH_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleData);
  const [mode, setMode] = useState<XMode>("auto");
  const [markers, setMarkers] = useState(true);
  const [zero, setZero] = useState(false);
  const [headerChoice, setHeaderChoice] = useState<boolean | null>(null);
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: strings.exampleXTitle,
    yTitle: strings.exampleYTitle,
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const table = parseLabeledRows(raw, { header: headerChoice ?? undefined });
  const seriesCount = Math.min(table.seriesCount, MAX_SERIES);
  const { rows, numeric, sorted, fellBack } = prepareRows(
    table,
    mode,
    seriesCount
  );
  const names = seriesNames(table, (i) =>
    t(strings.seriesDefault, { number: fmt(i + 1) })
  ).slice(0, seriesCount);
  const head = [table.header?.[0]?.trim() || strings.xColumn, ...names];
  const notices: string[] = [];
  if (rows.length < 2) {
    notices.push(strings.enterData);
  }
  if (table.skipped.length > 0) {
    notices.push(
      tPlural(strings.skippedLines, locale, table.skipped.length, {
        lines: table.skipped.map((n) => fmt(n)).join(", "),
      })
    );
  }
  if (fellBack) {
    notices.push(strings.notNumeric);
  }
  if (sorted) {
    notices.push(strings.sortedByX);
  }
  if (rows.some((row) => row.values.includes(null))) {
    notices.push(strings.gapsNote);
  }
  if (table.seriesCount > MAX_SERIES) {
    notices.push(t(strings.tooManySeries, { max: fmt(MAX_SERIES) }));
  }

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.dataHint}
        id="lgm-data"
        label={strings.dataLabel}
        onChange={setRaw}
        rows={8}
        value={raw}
      />
      <Checkbox
        checked={headerChoice ?? table.autoHeader}
        label={strings.firstLineHeader}
        onChange={setHeaderChoice}
      />
      <div aria-live="polite" className="space-y-2">
        {rows.length >= 2 ? (
          <p className={HINT}>
            {tPlural(strings.pointsDetected, locale, rows.length, {
              count: fmt(rows.length),
            })}
            {" · "}
            {tPlural(strings.seriesDetected, locale, seriesCount, {
              count: fmt(seriesCount),
            })}
          </p>
        ) : null}
        {notices.map((notice) => (
          <Notice key={notice}>{notice}</Notice>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="lgm-x-mode"
          label={strings.xModeLabel}
          onChange={setMode}
          options={[
            { value: "auto", label: strings.xModeAuto },
            { value: "category", label: strings.xModeCategory },
            { value: "numeric", label: strings.xModeNumeric },
          ]}
          value={mode}
        />
        <div className="space-y-3 sm:pt-7">
          <Checkbox
            checked={markers}
            label={strings.markers}
            onChange={setMarkers}
          />
          <Checkbox
            checked={zero}
            label={strings.startAtZero}
            onChange={setZero}
          />
        </div>
      </div>
      {rows.length >= 2 ? (
        <ChartFigure
          apa={style.apa}
          data={toTsv([head, ...rows.map((row) => [row.label, ...row.values])])}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "line-graph")}
          labels={strings}
          note={style.note}
          table={{
            head,
            rows: rows.map((row) => [
              row.label,
              ...row.values.map((v) =>
                v === null ? MISSING : valueText(fmt, v)
              ),
            ]),
          }}
          title={style.title}
        >
          <LineChart
            description={describeLines(rows, names, strings, locale, fmt)}
            fmt={fmt}
            id="lgm-chart"
            label={style.title.trim() || strings.chartLabel}
            markers={markers}
            names={names}
            numeric={numeric}
            rows={rows}
            style={style}
            zero={zero}
          />
        </ChartFigure>
      ) : null}
      <ChartStyleFields
        axisLabels={strings}
        colorLabels={strings}
        idPrefix="lgm"
        labels={strings}
        style={style}
        update={update}
        valueLabel={strings.valueLabels}
      />
    </div>
  );
}
